import { useState, useEffect, useRef } from "react";

const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes — visibilitychange covers fast cases
const API_BASE = import.meta.env.VITE_BACKEND_URL || "";
const DISMISS_KEY = "altiv_update_dismissed_date";
const VERSION_KEY = "altiv_app_version";

function todayString() {
  return new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
}

function isDismissedToday() {
  try {
    return localStorage.getItem(DISMISS_KEY) === todayString();
  } catch {
    return false;
  }
}

function getStoredVersion() {
  try {
    return localStorage.getItem(VERSION_KEY);
  } catch {
    return null;
  }
}

function setStoredVersion(v) {
  try {
    localStorage.setItem(VERSION_KEY, v);
  } catch {}
}

export function useAppVersion() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const currentVersionRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchVersion() {
      try {
        const res = await fetch(`${API_BASE}/api/version`, { cache: "no-store" });
        if (!res.ok) {
          console.warn("[useAppVersion] /api/version returned", res.status);
          return;
        }
        const { version } = await res.json();
        if (cancelled) return;

        currentVersionRef.current = version;

        const stored = getStoredVersion();
        if (stored === null) {
          // First ever load — store current version as baseline, no banner
          setStoredVersion(version);
        } else if (stored !== version) {
          // Version changed since user last acknowledged an update
          if (!isDismissedToday()) {
            setUpdateAvailable(true);
          }
        }
      } catch (err) {
        console.warn("[useAppVersion] Failed to fetch version:", err?.message || err);
      }
    }

    // Check immediately on mount and on every poll tick
    fetchVersion();
    const timer = setInterval(fetchVersion, POLL_INTERVAL_MS);

    // Also re-check when the user comes back to the tab (e.g. after a deploy)
    function onVisibilityChange() {
      if (document.visibilityState === "visible") fetchVersion();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  function dismiss() {
    setUpdateAvailable(false);
    try {
      localStorage.setItem(DISMISS_KEY, todayString());
    } catch {}
  }

  function reload() {
    // Advance stored version before reloading so the banner doesn't immediately reappear
    if (currentVersionRef.current) setStoredVersion(currentVersionRef.current);
    window.location.reload();
  }

  return { updateAvailable, dismiss, reload };
}
